import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { Button } from '@nextui-org/react';
import React from 'react';

import { ServerName, ServerUUID } from '@common/types';
import { ModernModal } from '@portal/components/common/ModernModal.ui';
import { TypographyUI } from '@portal/components/common/Typography.ui';

export interface BatchSslResult {
  serverUUID: ServerUUID;
  serverName: ServerName;
  success: boolean;
  message: string;
}

export interface BatchSslResultsData {
  results: BatchSslResult[];
  summary: {
    total: number;
    success: number;
    failed: number;
  };
}

interface BatchSslResultsModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  resultsData: BatchSslResultsData | null;
}

export const BatchSslResultsModal: React.FC<BatchSslResultsModalProps> = ({ isOpen, onOpenChange, resultsData }) => {
  if (!resultsData) return null;

  const { results, summary } = resultsData;
  const successResults = results.filter(r => r.success);
  const failedResults = results.filter(r => !r.success);

  const icon = <CheckCircleIcon className='h-4 w-4' />;
  const subtitle = 'Summary of the batch SSL password update operation';

  const footer = (
    <Button color='primary' onPress={onOpenChange}>
      Close
    </Button>
  );

  return (
    <ModernModal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title='Batch SSL Update Results'
      subtitle={subtitle}
      icon={icon}
      size='3xl'
      footer={footer}
    >
      <div className='flex flex-col gap-3'>
        {/* Summary Section */}
        <div className='bg-gray-50 p-3 rounded-lg'>
          <TypographyUI variant='h6' color='blue-gray' className='mb-2'>
            Summary
          </TypographyUI>
          <div className='flex items-center gap-6'>
            <div className='flex items-center gap-2'>
              <TypographyUI variant='h4' color='blue-gray'>
                {summary.total}
              </TypographyUI>
              <TypographyUI variant='caption' color='gray'>
                Total
              </TypographyUI>
            </div>
            <div className='flex items-center gap-2'>
              <TypographyUI variant='h4' color='green'>
                {summary.success}
              </TypographyUI>
              <TypographyUI variant='caption' color='gray'>
                Successful
              </TypographyUI>
            </div>
            <div className='flex items-center gap-2'>
              <TypographyUI variant='h4' color='red'>
                {summary.failed}
              </TypographyUI>
              <TypographyUI variant='caption' color='gray'>
                Failed
              </TypographyUI>
            </div>
          </div>
        </div>

        {/* Failed Results Section */}
        {failedResults.length > 0 && (
          <div>
            <TypographyUI variant='h6' color='red' className='mb-2 flex items-center gap-2'>
              <XCircleIcon className='h-4 w-4' />
              Failed Updates ({failedResults.length})
            </TypographyUI>
            <div className='space-y-2 max-h-40 overflow-y-auto'>
              {failedResults.map(result => (
                <div key={result.serverUUID} className='bg-red-50 p-2 rounded border border-red-200'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <TypographyUI variant='caption' className='font-semibold text-red-800'>
                        {result.serverName}
                      </TypographyUI>
                      <TypographyUI variant='caption' color='red' className='mt-1'>
                        {result.message}
                      </TypographyUI>
                    </div>
                    <XCircleIcon className='h-3 w-3 text-red-500 flex-shrink-0 ml-2' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Success Results Section */}
        {successResults.length > 0 && (
          <div>
            <TypographyUI variant='h6' color='green' className='mb-2 flex items-center gap-2'>
              <CheckCircleIcon className='h-4 w-4' />
              Successful Updates ({successResults.length})
            </TypographyUI>
            <div className='space-y-2 max-h-40 overflow-y-auto'>
              {successResults.map(result => (
                <div key={result.serverUUID} className='bg-green-50 p-2 rounded border border-green-200'>
                  <div className='flex items-start justify-between'>
                    <div className='flex-1'>
                      <TypographyUI variant='caption' className='font-semibold text-green-800'>
                        {result.serverName}
                      </TypographyUI>
                      <TypographyUI variant='caption' color='green' className='mt-1'>
                        {result.message}
                      </TypographyUI>
                    </div>
                    <CheckCircleIcon className='h-3 w-3 text-green-500 flex-shrink-0 ml-2' />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ModernModal>
  );
};
